<template>
  <div>
    <b-row>
      <b-col>
        <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item @click="$router.push({ name: 'Home' })">
              首頁
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              參數設定
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              CYBERBIZ 會員等級批量調整（逐筆同步）
            </b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-card class="mb-3">
          <div class="mb-3">
            <div>
              <h4 class="font-weight-bold mb-2">CYBERBIZ 會員等級批量調整（逐筆同步）</h4>
              <p class="mb-2 text-muted">
                上傳 Excel 後，系統會逐筆同步會員等級到 CYBERBIZ。
              </p>
            </div>
          </div>

          <div class="import-form">
            <b-form-group label-for="merchant-select">
              <label class="field-label" for="merchant-select">品牌</label>
              <b-form-select
                id="merchant-select"
                v-model="selectedMerchantId"
                :options="merchantOptions"
                :disabled="isFetchingMerchants"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    {{ isFetchingMerchants ? '載入中...' : '請選擇品牌' }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <b-form-group label-for="level-file">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="field-label mb-0" for="level-file">Excel 檔案</label>
              </div>
              <b-form-file
                id="level-file"
                v-model="selectedFile"
                accept=".xlsx,.xls"
                placeholder="尚未選擇檔案"
                browse-text="瀏覽"
                plain
              ></b-form-file>
              <small class="form-text text-muted">
                格式：第 1 欄為 CYBERBIZ 會員編號，第 2 欄為 CYBERBIZ 等級代號。支援 xlsx / xls，可包含表頭。
              </small>
              <small class="form-text text-danger">
                請勿填入 內部系統會員編號 或 內部系統會員等級名稱。
              </small>
              <b-button
                class="p-0"
                variant="link"
                size="sm"
                @click="getSampleExcelFile(null, '/excel/cyberbiz_level_change.xlsx')"
              >
                <i class="fa fa-file-text mr-1" aria-hidden="true"></i>
                下載範例檔案
              </b-button>
            </b-form-group>
          </div>

          <div class="d-flex justify-content-end">
            <b-button
              variant="primary"
              :disabled="!canSubmit"
              @click="handlePreviewSubmit"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
              ></b-spinner>
              <i
                v-else
                class="mdi mdi-sync mr-1"
                aria-hidden="true"
              ></i>
              {{ isSubmitting ? '同步中...' : '開始同步' }}
            </b-button>
          </div>
        </b-card>

        <b-card v-if="showResult" class="mb-3">
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
            <div>
              <h5 class="font-weight-bold mb-1">同步結果</h5>
              <p class="mb-0 text-muted">以下為後端回傳的同步統計與錯誤列。</p>
            </div>
          </div>

          <b-row class="mb-3">
            <b-col
              v-for="item in summaryItems"
              :key="item.label"
              cols="12"
              md="4"
              class="mb-2"
            >
              <div :class="['summary-item', `summary-item-${item.variant}`]">
                <div class="summary-label">{{ item.label }}</div>
                <div class="summary-value">{{ item.value }}</div>
                <div class="summary-description">{{ item.description }}</div>
              </div>
            </b-col>
          </b-row>

          <b-table
            v-if="errorRows.length > 0"
            responsive
            striped
            hover
            :items="errorRows"
            :fields="errorFields"
          >
            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.item.status)">
                {{ getStatusText(data.item.status) }}
              </b-badge>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapState } from "vuex";
import PermissionChecker from "@/utils/PermissionChecker";
import * as consts from "@/consts";
import { getSampleExcelFile } from "@/utils/dashboardUtils";
import cyberbizApi from "@/apis/cyberbiz";

export default {
  name: "CyberbizCustomerLevelChange",

  data() {
    return {
      merchants: [],
      selectedMerchantId: null,
      selectedFile: null,
      showResult: false,
      isFetchingMerchants: false,
      isSubmitting: false,
      summaryItems: [
        {
          label: "總筆數",
          value: 0,
          variant: "total",
          description: "有效匯入列總數",
        },
        {
          label: "成功",
          value: 0,
          variant: "success",
          description: "成功同步筆數",
        },
        {
          label: "失敗",
          value: 0,
          variant: "failed",
          description: "同步失敗筆數",
        },
      ],
      errorFields: [
        { key: "row_number", label: "Excel 列號" },
        { key: "member_outer_code", label: "會員編號" },
        { key: "level_code", label: "等級代號" },
        { key: "status", label: "狀態" },
        { key: "error_message", label: "錯誤訊息" },
      ],
      errorRows: [],
    };
  },

  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    orgId() {
      return this.organization.id;
    },
    merchantOptions() {
      return this.merchants.map((merchant) => ({
        value: merchant.merchant_id,
        text: merchant.merchant_name,
      }));
    },
    selectedMerchantName() {
      const merchant = this.merchantOptions.find((option) => option.value === this.selectedMerchantId);
      return merchant ? merchant.text : null;
    },
    selectedFileName() {
      return this.selectedFile ? this.selectedFile.name : null;
    },
    canSubmit() {
      return Boolean(this.selectedMerchantId && this.selectedFile && !this.isSubmitting);
    },
  },

  mounted() {
    if (!this.checkPermission([consts.CYBERBIZ_CUSTOMER_LEVEL_CHANGE])) {
      this.$swal({
        icon: "error",
        title: "權限不足",
        text: "你沒有權限訪問此頁面",
      }).then(() => {
        this.$router.push({ name: "Home" });
      });
      return;
    }

    this.fetchMerchants();
  },

  methods: {
    getSampleExcelFile,
    async fetchMerchants() {
      try {
        this.isFetchingMerchants = true;
        const { data } = await cyberbizApi.getMerchants(this.orgId);
        this.merchants = data.data || [];

        if (this.merchants.length === 1) {
          this.selectedMerchantId = this.merchants[0].merchant_id;
        }
      } catch (error) {
        console.error("Failed to fetch merchants:", error);
        this.$swal({
          icon: "error",
          title: "載入失敗",
          text: "載入品牌列表失敗",
        });
      } finally {
        this.isFetchingMerchants = false;
      }
    },
    handlePreviewSubmit() {
      if (!this.isValidExcelFile(this.selectedFile)) {
        this.$swal({
          icon: "warning",
          title: "檔案格式錯誤",
          text: "僅支援 xlsx / xls 檔案",
        });
        return;
      }

      this.$swal({
        icon: "warning",
        title: "確認同步會員等級？",
        html: `
          <div class="text-left">
            <div class="mb-2">此操作會將檔案中的會員等級同步至 CYBERBIZ，請確認品牌與檔案正確。</div>
            <div><strong>品牌：</strong>${this.selectedMerchantName}</div>
            <div><strong>檔案：</strong>${this.selectedFileName}</div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "確認同步",
        cancelButtonText: "取消",
        reverseButtons: true,
      }).then(async (result) => {
        if (result.value || result.isConfirmed) {
          await this.importCustomerLevels();
        }
      });
    },
    async importCustomerLevels() {
      try {
        this.isSubmitting = true;
        this.showResult = false;

        const { data } = await cyberbizApi.importCustomerLevels(this.orgId, {
          merchant_id: this.selectedMerchantId,
          file: this.selectedFile,
        });

        this.applyImportResult(data.data || {});
        this.showResult = true;

        this.$swal({
          icon: "success",
          title: "同步完成",
          text: "會員等級同步已完成，請查看下方結果。",
        });
      } catch (error) {
        console.error("Cyberbiz customer level import failed:", error);
        this.$swal({
          icon: "error",
          title: "同步失敗",
          text: this.getErrorMessage(error),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    applyImportResult(result) {
      const summary = result.summary || {};
      this.summaryItems = [
        {
          label: "總筆數",
          value: summary.total_rows || 0,
          variant: "total",
          description: "有效匯入列總數",
        },
        {
          label: "成功",
          value: summary.success_rows || 0,
          variant: "success",
          description: "同步成功筆數",
        },
        {
          label: "失敗",
          value: summary.failed_rows || 0,
          variant: "failed",
          description: "同步失敗筆數",
        },
      ];
      this.errorRows = result.error_rows || [];
    },
    isValidExcelFile(file) {
      if (!file || !file.name) {
        return false;
      }

      return /\.(xlsx|xls)$/i.test(file.name);
    },
    getErrorMessage(error) {
      const responseData = error && error.response ? error.response.data : null;

      if (!responseData) {
        return "同步失敗，請稍後再試";
      }

      if (responseData.errors) {
        return Object.keys(responseData.errors)
          .map((field) => responseData.errors[field].join("、"))
          .join("；");
      }

      if (responseData.message) {
        return responseData.message;
      }

      if (responseData.error) {
        return responseData.error;
      }

      return "同步失敗，請稍後再試";
    },
    getStatusText(status) {
      const statusMap = {
        sync_failed: "同步失敗",
        invalid_input: "資料格式錯誤",
        customer_not_found: "找不到會員",
      };

      return statusMap[status] || status;
    },
    getStatusVariant(status) {
      const variantMap = {
        sync_failed: "danger",
        invalid_input: "warning",
        customer_not_found: "secondary",
      };

      return variantMap[status] || "light";
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
  },
};
</script>

<style scoped lang="scss">
.import-form {
  max-width: 640px;
}

.field-label {
  color: #1f2933;
  font-size: 15px;
  font-weight: 700;
}

.summary-item {
  min-height: 74px;
  padding: 10px 14px;
  border: 1px solid #dbe4ea;
  border-radius: 4px;
  background: #ffffff;
}

.summary-item-total {
  border-left: 4px solid #607d8b;
}

.summary-item-success {
  border-left: 4px solid #2e7d32;
}

.summary-item-failed {
  border-left: 4px solid #c62828;
}

.summary-label {
  color: #52616b;
  font-size: 13px;
  font-weight: 700;
}

.summary-value {
  margin-top: 2px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.summary-item-success .summary-value {
  color: #2e7d32;
}

.summary-item-failed .summary-value {
  color: #c62828;
}

.summary-description {
  margin-top: 2px;
  color: #7b8794;
  font-size: 12px;
}

.result-badge {
  font-size: 12px;
  padding: 6px 10px;
}
</style>
